<template>
  <div class="signin-modal__shell" :class="{ 'signin-modal__shell--plain': hideBranding }" data-testid="signin-modal-wrapper">
    <section class="signin-modal__form" aria-label="Sign in to Zight">
      <div v-if="!hideBranding" class="signin-modal__branding" :data-testid="view === 'signin' ? 'sign-in-modal-branding' : undefined">
        <ImageSvg name="general.logoWithText" />
      </div>

      <!-- Forgot password form -->
      <template v-if="view === 'forgot'">
        <TitleSeparator class="signin-modal__divider">Forgot password?</TitleSeparator>
        <p class="signin-modal__helper-text">You can reset your password here.</p>
        <Form @submit="onForgotSubmit" :validation-schema="forgotSchema">
          <fieldset class="signin-modal__fields">
            <div class="signin-modal__field">
              <label htmlFor="forgotEmailInput" class="signin-modal__label">Email</label>
              <Field type="text" name="email" id="forgotEmailInput" class="signin-modal__input" />
              <ErrorMessage as="p" class="signin-modal__error" name="email" />
            </div>
            <Button
              class="!w-full signin-modal__submit"
              :loading="forgotInProgress && forgotAction === 'reset'"
              :disabled="forgotInProgress"
              type="solid"
              variant="blue"
              :submit="true"
              @click="forgotAction = 'reset'"
            >
              Reset password
            </Button>
            <TitleSeparator class="signin-modal__divider signin-modal__divider--compact">Or</TitleSeparator>
            <Button
              class="!w-full signin-modal__submit"
              :loading="forgotInProgress && forgotAction === 'magic'"
              :disabled="forgotInProgress"
              type="ghost"
              variant="blue"
              :submit="true"
              @click="forgotAction = 'magic'"
            >
              Send a sign in link via email
            </Button>
          </fieldset>
        </Form>
        <p class="signin-modal__signup">
          <a href="#" @click.stop.prevent="view = 'signin'">Back to Sign in page</a>
        </p>
      </template>

      <!-- Forgot password sent confirmation -->
      <template v-else-if="view === 'sent'">
        <TitleSeparator class="signin-modal__divider">Check your email</TitleSeparator>
        <p class="signin-modal__helper-text">{{ sentMessage }}</p>
        <p class="signin-modal__signup">
          <a href="#" @click.stop.prevent="view = 'signin'">Back to sign in</a>
        </p>
      </template>

      <!-- Sign in form -->
      <template v-else>
        <TitleSeparator class="signin-modal__divider">Sign in with</TitleSeparator>
        <SocialAuth :redirect-url="props.redirectUrl" @action="onSocialAction" :in-progress="socialInProgress" />
        <TitleSeparator class="signin-modal__divider signin-modal__divider--compact">Or</TitleSeparator>

        <Form @submit="onSubmit" :validation-schema="schema">
          <fieldset class="signin-modal__fields">
            <div class="signin-modal__field">
              <label htmlFor="emailInput" class="signin-modal__label">Email</label>
              <Field type="text" name="email" id="emailInput" data-testid="sign-in-email" class="signin-modal__input" />
              <ErrorMessage as="p" class="signin-modal__error" name="email" />
            </div>
            <div class="signin-modal__field">
              <label htmlFor="passwordInput" class="signin-modal__label">
                Password
                <a href="#" @click.stop.prevent="view = 'forgot'" data-testid="sign-in-forgot-password" class="signin-modal__forgot">Forgot password?</a>
              </label>
              <Field type="password" name="password" id="passwordInput" data-testid="sign-in-password" class="signin-modal__input" />
              <ErrorMessage as="p" class="signin-modal__error" name="password" />
            </div>
            <Button
              class="!w-full signin-modal__submit"
              data-testid="sign-in"
              :loading="requestInProgress"
              :disabled="requestInProgress"
              type="solid"
              variant="blue"
              :submit="true"
            >
              Sign in
            </Button>
          </fieldset>
        </Form>

        <p v-if="!hideBranding" class="signin-modal__signup" data-testid="sign-in-modal-signup-link">
          New to Zight?
          <a href="#" @mousedown.prevent @click.stop.prevent="showSignUpModal({ redirectUrl: redirectUrl })" data-testid="sign-up-show-modal"
            >Sign up for free</a
          >
        </p>
      </template>
    </section>

    <AuthIntegrationsPromo v-if="!hideBranding" test-id="signin-promo-box" />
  </div>
</template>
<script setup lang="ts">
import { object, string } from 'yup';
import { Form, Field, ErrorMessage } from 'vee-validate';
import { useUserStore } from '@/store/useUserStore';
import SocialAuth from './auth_dialog/Social.vue';
import { useAuth } from '@/composables/useAuth';
import TitleSeparator from './auth_dialog/TitleSeparator.vue';
import { useToast } from '@/composables/useToast';
import { useAuthModals } from '@/composables/useAuthModals';
import { useBranding } from '@/composables/useBranding';
import { ref } from 'vue';
import { Button, ImageSvg } from '@/storybook';
import AuthIntegrationsPromo from './AuthIntegrationsPromo.vue';
import { Api } from '@/Api';

const props = defineProps<{
  redirectUrl?: string;
}>();

const userStore = useUserStore();

const { showSignUpModal } = useAuthModals();
const { hideBranding } = useBranding();
const requestInProgress = ref(false);
const forgotInProgress = ref(false);
const forgotAction = ref<'reset' | 'magic'>('reset');
const view = ref<'signin' | 'forgot' | 'sent'>('signin');
const sentMessage = ref('');

const schema = object().shape({
  email: string().email().required(),
  password: string().min(6).required(),
});

const forgotSchema = object().shape({
  email: string().email().required(),
});

const { onSocialAction, inProgress: socialInProgress } = useAuth(props.redirectUrl);

const { error } = useToast();
const emits = defineEmits(['close']);

const onForgotSubmit = async (data: { email: string }) => {
  try {
    forgotInProgress.value = true;
    const url = forgotAction.value === 'magic' ? '/api/v5/magic_links' : '/api/v4/account/forgot_password';
    const response = await Api.post(url, { email: data.email });
    sentMessage.value = response.data?.metadata?.message || 'Check your email for instructions.';
    view.value = 'sent';
  } catch {
    error('Something went wrong. Please try again.');
  } finally {
    forgotInProgress.value = false;
  }
};

const onSubmit = async (data: any) => {
  try {
    requestInProgress.value = true;
    const response = await userStore.signIn(data);

    if (response && props.redirectUrl) {
      window.location.assign(props.redirectUrl);
      return;
    }

    if (response && response.metadata && response.metadata.redirectTo) {
      window.location.assign(response.metadata.redirectTo);
      return;
    }

    await userStore.load();

    requestInProgress.value = false;
    emits('close');
  } catch (err: any) {
    requestInProgress.value = false;
    error(err.message);
  }
};
</script>
<style scoped>
.signin-modal__shell {
  width: min(100vw - 2rem, 55rem);
  display: grid;
  grid-template-columns: minmax(20rem, 1fr) minmax(18rem, 0.9fr);
  gap: 0;
  overflow: hidden;
  border-radius: 1.5rem;
  background: var(--white-copernicus, #fff);
  box-shadow:
    0 1.25rem 3.75rem rgba(15, 23, 42, 0.22),
    0 0 0 1px rgba(148, 163, 184, 0.18);
}

.signin-modal__shell--plain {
  width: min(100vw - 2rem, 28rem);
  grid-template-columns: 1fr;
  display: flex;
  flex-direction: column;
  padding: 3rem 3.75rem 2rem;
  background: var(--white-copernicus, #fff);
  color: var(--zinc-900, #18181b);
}

.signin-modal__form {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16.5px;
  padding: 3rem 3.75rem;
  background: var(--white-copernicus, #fff);
  color: var(--zinc-900, #18181b);
}

.signin-modal__branding {
  display: flex;
  justify-content: center;
}

.signin-modal__branding :deep(svg),
.signin-modal__branding :deep(img) {
  max-height: 2.75rem;
}

.signin-modal__divider {
  --divider-background: var(--white-copernicus, #fff);
  --divider-stroke: #d8e0ec;
  --divider-color: #64748b;
  margin-block: 0 !important;
}

.signin-modal__divider--compact {
  margin-block: 0.25rem !important;
}

.signin-modal__fields {
  display: flex;
  flex-direction: column;
  gap: 16.5px;
  margin: 0;
  padding: 0;
  border: none;
}

.signin-modal__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.signin-modal__label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #334155;
  font-size: 0.875rem;
  font-weight: 700;
}

.signin-modal__forgot {
  color: #2563eb;
  font-weight: 400;
  text-decoration: none;
  font-size: 0.875rem;
}

.signin-modal__forgot:hover {
  color: #60a5fa;
}

.signin-modal__input {
  width: 100%;
  height: 34px;
  padding: 0 0.75rem;
  border: 1px solid #cdd5e1;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  background: #ffffff;
  color: #0f172a;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.signin-modal__input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 0.25rem rgba(37, 99, 235, 0.18);
}

.signin-modal__error {
  margin-block-start: 0.25rem;
  color: #64748b;
  font-size: 0.875rem;
}

.signin-modal__submit {
  min-height: 3.25rem;
  border-radius: 100px;
  font-size: 14px !important;
  font-weight: 700 !important;
}

.signin-modal__helper-text {
  color: #475569;
  font-size: 0.875rem;
  margin-block: 0;
}

.signin-modal__signup {
  margin-block: 0;
  color: #475569;
  font-size: 0.875rem;
  text-align: center;
}

.signin-modal__signup a {
  color: #2563eb;
  text-decoration: none;
  border-radius: 0.25rem;
}

.signin-modal__signup a:hover {
  color: #60a5fa;
}

.signin-modal__signup a:focus-visible {
  outline: none;
  box-shadow: 0 0 0 0.25rem rgba(37, 99, 235, 0.22);
}

@media (max-width: 760px) {
  .signin-modal__shell {
    width: min(100vw - 2.5rem, 28rem);
    margin-block-start: 0.75rem;
    grid-template-columns: 1fr;
    max-height: calc(100vh - 3rem);
    overflow-y: auto;
    border-radius: 1.25rem;
  }

  .signin-modal__form {
    padding: 2rem 1.5rem 1.5rem;
  }
}
</style>
